type ProgressStudent = {
  name?: string;
  progress?: string | number;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

type Params = {
  students?: ProgressStudent[] | null;
  t: Translate;
};

type ProgressGroup = {
  ariaLabel: string;
  key: string;
  left: string;
  progressLabel: string;
  students: ProgressStudent[];
};

type UseHorizontalStudentsProgress = {
  groups: ProgressGroup[];
  listLabel: string;
};

// Desplazamiento para centrar el grupo según el ancho de sus avatares.
function getOffset(count: number): number {
  if (count === 1) return 16;
  if (count === 2) return 32;
  return 48;
}

function parseProgress(progress: ProgressStudent['progress']): number {
  const value = Number.parseFloat(String(progress));
  return Number.isNaN(value) ? 0 : value;
}

export function useHorizontalStudentsProgress({ students, t }: Params): UseHorizontalStudentsProgress {
  const groupsByProgress = new Map<number, ProgressStudent[]>();

  (students ?? []).forEach((student) => {
    const value = parseProgress(student.progress);
    const group = groupsByProgress.get(value) ?? [];
    group.push(student);
    groupsByProgress.set(value, group);
  });

  // De menor a mayor: así el orden de lectura y de Tab coincide con el visual.
  const groups = Array.from(groupsByProgress.entries())
    .sort(([a], [b]) => a - b)
    .map(([value, group]) => {
      const progressLabel = `${value.toFixed(1)}%`;
      const names = group
        .map((student) => student.name)
        .filter(Boolean)
        .join(', ');
      const ariaLabel = names
        ? t('scorm:X students at progress with names', { count: group.length, names, progress: progressLabel })
        : t('scorm:X students at progress', { count: group.length, progress: progressLabel });

      return {
        ariaLabel,
        key: String(value),
        left: `calc(${value}% - ${getOffset(group.length)}px)`,
        progressLabel,
        students: group,
      };
    });

  return { groups, listLabel: t('scorm:Progress') };
}
